import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import { FactorControls } from "./FactorControls";
import { competitionScore } from "./utils";

interface CompetitionScoringCardProps {
  competitionMax: number;
  onCompetitionMaxChange: (v: number) => void;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}

const COMPETITION_ROWS = [100, 350, 1000, 3000, 10000, 20000, 30000, 50000, 100000];

export function CompetitionScoringCard({
  competitionMax,
  onCompetitionMaxChange,
  enabled,
  onEnabledChange,
}: CompetitionScoringCardProps) {
  return (
    <Card id="kps-module-competition" className={cn(!enabled && "bg-muted/30 order-1")}>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            icon={Users}
            title="Competition Scoring"
            description="Number of competing ASINs indexed on the keyword, scored on a bell curve that peaks around 10,000. Too few competitors signals thin demand; too many signals a saturated keyword."
          />
          <FactorControls
            id="kps-toggle-competition"
            enabled={enabled}
            onEnabledChange={onEnabledChange}
            value={competitionMax}
            onValueChange={onCompetitionMaxChange}
          />
        </div>

        <div className={cn("space-y-4", !enabled && "opacity-50 pointer-events-none select-none")}>
          <Alert variant="default">
            <AlertDescription className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Formula</p>
              <div className="text-sm tabular-nums text-foreground space-y-0.5">
                <p>
                  <span className="text-muted-foreground">d = </span>
                  <span className="font-semibold">log10(competitors) − log10(10,000)</span>
                </p>
                <p>
                  <span className="text-muted-foreground">sigma = </span>
                  <span className="font-semibold">0.85 if d &lt; 0, else 0.41</span>
                </p>
                <p>
                  <span className="text-muted-foreground">score = </span>
                  <span className="font-semibold tabular-nums">{competitionMax}</span>
                  <span className="text-muted-foreground"> × </span>
                  <span className="font-semibold">e^(−d² / (2 × sigma²))</span>
                </p>
              </div>
            </AlertDescription>
          </Alert>

          <div className="space-y-0">
            <div className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 h-9 border-b border-border">
              <span className="text-xs font-semibold text-foreground">Competing ASINs</span>
              <span className="text-xs font-semibold text-foreground text-center">Points Earned</span>
              <span className="text-xs font-semibold text-foreground text-right">Score</span>
            </div>
            {COMPETITION_ROWS.map((competitors) => {
              const score = competitionScore(competitors, competitionMax);
              const pct = competitionMax > 0 ? (score / competitionMax) * 100 : 0;
              const isPeak = competitors === 10000;
              const formatted = score.toFixed(1).replace(/\.0$/, "");
              return (
                <div
                  key={competitors}
                  className={cn(
                    "grid grid-cols-[180px_1fr_100px] gap-4 items-center px-3 py-3 border-b border-border last:border-b-0 -mx-3",
                    isPeak && "bg-status-success-soft"
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground tabular-nums">{competitors.toLocaleString()}</span>
                    {isPeak && (
                      <Badge className="bg-[hsl(155_45%_75%)] text-foreground hover:bg-[hsl(155_45%_75%)]">
                        Sweet spot
                      </Badge>
                    )}
                  </div>
                  <Progress value={pct} className="h-2" indicatorClassName="bg-primary" />
                  <span className="text-sm tabular-nums text-right">
                    <span className="text-foreground">{formatted}</span>
                    <span className="text-xs text-muted-foreground"> / {competitionMax}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
